import { Children, useEffect, useRef, useState } from "react"
import { FeatureGroup, Polygon, Popup, Tooltip } from "react-leaflet"
import { EditControl } from "react-leaflet-draw"
import { useDispatch, useSelector } from "react-redux"
import { useUpdateZone } from "./hooks/useUpdateZone"
import { getIntersectPolygon } from "./utils"
import { ActionDeletedPolygon } from "redux/optime/optime"

interface PolygonProps {
  polygonData: any
  setSelectedPolygons: any
  getZoneInfo: any
  selectedHub: any
}

function MapActions({
  polygonData,
  setSelectedPolygons,
  getZoneInfo,
  selectedHub,
}: PolygonProps) {
  const { editZone, deletedPoly } = useSelector(
    (state: any) => state.optimeModal
  )
  const { areaType, selectedZone } = useSelector(
    (state: any) => state.customGeo
  )
  const { CurrentHub } = useSelector((state: any) => state.currentHubInfo)
  const { geoData } = useSelector((state: any) => state.customGeo)

  const LeafletControllerRef = useRef<Record<string, any>>({})
  const featureGroupRef = useRef(null)
  const { updateAPI } = useUpdateZone()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showPopUp, setShowPopUp] = useState(false)
  const childPolygons = useRef<any>([])
  const dispatch = useDispatch()

  useEffect(() => {
    if (editZone) {
      setShowPopUp(true)
    } else {
      setShowPopUp(false)
      cancelAllEdit()
    }
  }, [editZone, selectedId])

  useEffect(() => {
    if (deletedPoly !== -1) {
      delete LeafletControllerRef.current[deletedPoly]
    }
    dispatch(ActionDeletedPolygon(-1))
    cancelAllEdit()
  }, [polygonData])

  function onSelectPolygon(polygon) {
    setSelectedId(polygon.id)
    const controller = LeafletControllerRef.current[polygon.id]
    if (controller) {
      controller._toolbars.edit._modes?.edit?.handler?.enable()
      setShowPopUp(true)
    }
  }

  const onMountedRect = (e: any, id: string) => {
    LeafletControllerRef.current[id] = e
  }

  const onSaveZoneHandler = async (item) => {
    const controller = LeafletControllerRef.current[item.id]
    if (!controller) return

    const layers: any = Object.values(
      controller._toolbars.edit.options.featureGroup._layers
    )[0]

    const updatedZone = [
      layers?._latlngs[0].map((item: any) => [item.lat, item.lng]),
    ]

    const flattenArr = updatedZone.flat()
    flattenArr.push(flattenArr[0])
    const createdZone = flattenArr

    const finalPoly = getIntersectPolygon(
      childPolygons.current[0].polygonCordinate,
      createdZone,
      childPolygons.current.filter((elem) => elem.id !== item.id)
    )
    if (finalPoly.length !== 0 && finalPoly.flat(1).length > 4) {
      await updateAPI({ ...item, createdZone: finalPoly })
      await getZoneInfo()

      setSelectedId(null)
      cancelAllEdit()
      setShowPopUp(false)
    }
  }

  const handelCancelEdit = (id: string) => {
    const controller = LeafletControllerRef.current[id]
    if (controller) {
      controller._toolbars?.edit?._modes.edit?.handler?.enable()
      controller._toolbars?.edit?._modes.edit?.handler?.revertLayers()
      controller._toolbars?.edit?._modes.edit?.handler?.disable()
    }
  }

  function cancelAllEdit() {
    Object.values(LeafletControllerRef.current).forEach((controller: any) => {
      controller._toolbars?.edit?._modes?.edit?.handler?.enable()
      controller._toolbars?.edit?._modes?.edit?.handler?.revertLayers()
      controller._toolbars?.edit?._modes?.edit?.handler?.disable()
    })
  }

  useEffect(() => {
    if (polygonData.length !== 0) {
      childPolygons.current = polygonData
    }
  }, [polygonData])

  useEffect(() => {
    LeafletControllerRef.current = {}
  }, [CurrentHub?.id])

  useEffect(() => {
    if (!selectedZone?.id) {
      const updatedPolygons = polygonData.map((item, index) => {
        return { ...item, selectedColor: item.color }
      })
      setSelectedPolygons(updatedPolygons)
      return
    }
    const updatedPolygons = polygonData.map((item, index) => {
      if (index === 0) {
        return item
      }
      if (selectedZone && item.id === selectedZone.id) {
        return { ...item, selectedColor: "blue" }
      }
      return { ...item, selectedColor: item.color }
    })
    setSelectedPolygons(updatedPolygons)
  }, [selectedZone])

  return (
    <>
      {Children.toArray(
        polygonData?.map((item: any, index: number) => (
          <FeatureGroup
            ref={featureGroupRef}
            key={`${item.id}${areaType}${geoData?.id}`}
          >
            <EditControl
              onMounted={(e) => onMountedRect(e, item.id)}
              position='topright'
              draw={{
                rectangle: false,
                polyline: false,
                marker: false,
                circle: false,
                circlemarker: false,
                polygon: false,
              }}
            />
            {item.polygonCordinate && (
              <Polygon
                key={item.id}
                eventHandlers={{
                  click: () => index !== 0 && onSelectPolygon(item),
                }}
                positions={item.polygonCordinate}
                pathOptions={{
                  color: item.selectedColor ?? item.color,
                  fillColor: item.selectedColor ?? item.color,
                }}
              >
                {item.name && <Tooltip sticky>{item.name}</Tooltip>}
              </Polygon>
            )}

            {showPopUp && selectedId === item.id && (
              <Popup>
                <div className='flex flex-col gap-4'>
                  <div className='flex w-full justify-evenly gap-2'>
                    <button
                      className='btn !px-[2rem] full-gray-btn shadow-lg w-[40px]'
                      onClick={() => {
                        handelCancelEdit(item.id)
                        setShowPopUp(false)
                      }}
                    >
                      لغو
                    </button>
                    <button
                      className='btn w-[40px] !px-[2rem] shadow-lg full-tomato-btn'
                      onClick={() => onSaveZoneHandler(item)}
                    >
                      ذخیره
                    </button>
                  </div>
                </div>
              </Popup>
            )}
          </FeatureGroup>
        ))
      )}
    </>
  )
}

export default MapActions
